import { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, AreaChart, Area } from 'recharts';
import { FaTrophy, FaDumbbell, FaChartLine, FaArrowLeft, FaFire, FaCalendarAlt } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const StatCard = ({ icon, label, value, color }) => (
  <Card className="bg-[#1a1a1a] border-white/5">
    <CardContent className="p-4 flex items-center gap-3">
      <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ backgroundColor: `${color}20` }}>
        {icon}
      </div>
      <div>
        <p className="text-xs text-gray-500">{label}</p>
        <p className="text-xl font-bold text-white">{value}</p>
      </div>
    </CardContent>
  </Card>
);

export default function WorkoutHistory({ user }) {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [records, setRecords] = useState([]);
  const [selectedExercise, setSelectedExercise] = useState(null);
  const [exerciseData, setExerciseData] = useState([]);
  const [loading, setLoading] = useState(true);

  const token = localStorage.getItem('deisycfit_token');
  const headers = { 'Authorization': `Bearer ${token}` };

  useEffect(() => {
    Promise.all([
      axios.get(`${API}/workouts/stats/summary`, { headers }),
      axios.get(`${API}/workouts/personal-records`, { headers })
    ]).then(([statsRes, recordsRes]) => {
      setStats(statsRes.data);
      setRecords(recordsRes.data.records || []);
      if (recordsRes.data.records?.length > 0) {
        loadExerciseProgress(recordsRes.data.records[0].exercise_name);
      }
    }).catch(console.error).finally(() => setLoading(false));
  }, []);

  const loadExerciseProgress = async (exerciseName) => {
    setSelectedExercise(exerciseName);
    try {
      const res = await axios.get(`${API}/workouts/progress/${encodeURIComponent(exerciseName)}`, { headers });
      const data = (res.data || []).map(d => ({
        date: new Date(d.workout_date).toLocaleDateString('es-CO', { day: '2-digit', month: 'short' }),
        peso: d.max_weight,
        reps: d.max_reps,
        volumen: d.total_volume
      }));
      setExerciseData(data);
    } catch (err) {
      console.error(err);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0a0a0a] flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-pink-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0a0a0a] text-white pb-24" data-testid="workout-history-page">
      {/* Header */}
      <div className="sticky top-0 z-20 bg-[#0a0a0a]/90 backdrop-blur-md border-b border-white/5 px-4 py-3">
        <div className="flex items-center gap-3">
          <button onClick={() => navigate(-1)} className="text-gray-400 hover:text-white">
            <FaArrowLeft />
          </button>
          <h1 className="text-lg font-bold">Historial de Progresion</h1>
        </div>
      </div>

      <div className="px-4 pt-4 space-y-5">
        {/* Stats Grid */}
        {stats && (
          <div className="grid grid-cols-2 gap-3" data-testid="stats-grid">
            <StatCard icon={<FaDumbbell className="text-pink-500" />} label="Entrenamientos" value={stats.total_workouts} color="var(--brand-primary)" />
            <StatCard icon={<FaFire className="text-orange-500" />} label="Racha actual" value={`${stats.current_streak} dias`} color="#F97316" />
            <StatCard icon={<FaChartLine className="text-cyan-500" />} label="Ejercicios" value={stats.unique_exercises} color="#06B6D4" />
            <StatCard icon={<FaCalendarAlt className="text-green-500" />} label="Este mes" value={stats.workouts_this_month} color="#22C55E" />
          </div>
        )}

        {/* Exercise Selector + Chart */}
        {records.length > 0 && (
          <Card className="bg-[#1a1a1a] border-white/5" data-testid="progress-chart-card">
            <CardHeader className="pb-2">
              <CardTitle className="text-base flex items-center gap-2">
                <FaChartLine className="text-pink-500" /> Progresion por Ejercicio
              </CardTitle>
            </CardHeader>
            <CardContent>
              {/* Exercise pills */}
              <div className="flex flex-wrap gap-2 mb-4 max-h-32 overflow-y-auto">
                {records.map((r) => (
                  <button key={r.exercise_name}
                    onClick={() => loadExerciseProgress(r.exercise_name)}
                    className={`text-xs px-3 py-1.5 rounded-full font-medium transition-all ${
                      selectedExercise === r.exercise_name
                        ? 'bg-pink-500 text-white'
                        : 'bg-white/5 text-gray-400 hover:bg-white/10'
                    }`}
                    data-testid={`exercise-pill-${r.exercise_name}`}>
                    {r.exercise_name.length > 25 ? r.exercise_name.slice(0, 25) + '...' : r.exercise_name}
                  </button>
                ))}
              </div>

              {/* Chart */}
              {exerciseData.length > 1 ? (
                <div className="h-48">
                  <ResponsiveContainer width="100%" height="100%">
                    <AreaChart data={exerciseData}>
                      <defs>
                        <linearGradient id="colorPeso" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor="var(--brand-primary)" stopOpacity={0.3} />
                          <stop offset="95%" stopColor="var(--brand-primary)" stopOpacity={0} />
                        </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
                      <XAxis dataKey="date" tick={{ fill: '#6B7280', fontSize: 10 }} />
                      <YAxis tick={{ fill: '#6B7280', fontSize: 10 }} width={35} />
                      <Tooltip
                        contentStyle={{ backgroundColor: '#1a1a1a', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '8px', color: '#fff' }}
                        formatter={(v, name) => [`${v} ${name === 'peso' ? 'kg' : name === 'reps' ? 'reps' : 'vol'}`, name === 'peso' ? 'Peso' : name === 'reps' ? 'Reps' : 'Volumen']}
                      />
                      <Area type="monotone" dataKey="peso" stroke="var(--brand-primary)" fill="url(#colorPeso)" strokeWidth={2} />
                    </AreaChart>
                  </ResponsiveContainer>
                </div>
              ) : (
                <p className="text-center text-gray-500 text-sm py-6">
                  {exerciseData.length === 1 ? 'Necesitas al menos 2 sesiones para ver la grafica' : 'Selecciona un ejercicio para ver su progresion'}
                </p>
              )}
            </CardContent>
          </Card>
        )}

        {/* Personal Records Table */}
        <Card className="bg-[#1a1a1a] border-white/5" data-testid="records-table">
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <FaTrophy className="text-amber-500" /> Records Personales
            </CardTitle>
          </CardHeader>
          <CardContent>
            {records.length === 0 ? (
              <p className="text-center text-gray-500 text-sm py-6">Aun no tienes records. Completa tu primer entrenamiento!</p>
            ) : (
              <div className="space-y-2">
                {/* Header */}
                <div className="grid grid-cols-[1fr_60px_50px_60px] gap-2 text-xs text-gray-500 font-semibold pb-1 border-b border-white/5">
                  <span>Ejercicio</span>
                  <span className="text-center">Peso</span>
                  <span className="text-center">Reps</span>
                  <span className="text-center">Sesiones</span>
                </div>
                {records.map((r, i) => (
                  <motion.div key={r.exercise_name}
                    initial={{ opacity: 0, x: -10 }} animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: i * 0.03 }}
                    className="grid grid-cols-[1fr_60px_50px_60px] gap-2 items-center py-2 border-b border-white/5 last:border-0"
                    data-testid={`record-row-${i}`}>
                    <span className="text-sm text-white truncate">{r.exercise_name}</span>
                    <span className="text-center text-sm font-bold text-pink-400">{r.max_weight > 0 ? `${r.max_weight}kg` : '-'}</span>
                    <span className="text-center text-sm font-bold text-cyan-400">{r.max_reps}</span>
                    <span className="text-center text-sm text-gray-400">{r.total_sessions}</span>
                  </motion.div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* No data state */}
        {records.length === 0 && !stats?.total_workouts && (
          <div className="text-center py-12">
            <FaDumbbell className="text-4xl text-gray-700 mx-auto mb-3" />
            <p className="text-gray-500">Completa entrenamientos en Data Progresion para ver tu historial</p>
            <Button onClick={() => navigate('/data-progresion')} className="mt-4 bg-pink-500 hover:bg-pink-600">
              Ir a Entrenar
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
